import React, { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Search, Plus, Pencil, Trash2, ChevronLeft, ChevronRight, Package, Hotel, Ticket, Bus, Plane, TrainFront, Camera } from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiErrorMessage } from "@/lib/api";
import { fmtEUR } from "@/pages/motor/shared";

const LIMIT = 20;
const EMPTY = { name: "", type: "actividad", provider_name: "", city: "", country: "", cost_tax_excl: "", cost_tax_incl: "", currency: "EUR", tier: "", description: "", notes: "" };
const PRODUCT_TYPES = ["alojamiento", "actividad", "entradas", "transfer", "tren", "vuelo"];
const TYPE_LABELS = { alojamiento: "Alojamiento", actividad: "Actividad", entradas: "Entradas", transfer: "Transfer", tren: "Tren", vuelo: "Vuelo" };
const TYPE_ICON = { alojamiento: Hotel, actividad: Camera, entradas: Ticket, transfer: Bus, tren: TrainFront, vuelo: Plane };

export const ProductosTab = () => {
  const qc = useQueryClient();
  const [term, setTerm] = useState("");
  const [q, setQ] = useState("");
  const [type, setType] = useState("all");
  const [country, setCountry] = useState("all");
  const [page, setPage] = useState(0);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [delTarget, setDelTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => { setQ(term); setPage(0); }, 350);
    return () => clearTimeout(t);
  }, [term]);

  const metaQ = useQuery({ queryKey: ["products-meta"], queryFn: async () => (await api.get("/products/meta")).data });
  const listQ = useQuery({
    queryKey: ["products", q, type, country, page],
    queryFn: async () => (await api.get("/products", { params: { q, type: type === "all" ? "" : type, country: country === "all" ? "" : country, skip: page * LIMIT, limit: LIMIT } })).data,
    keepPreviousData: true,
  });

  const items = listQ.data?.items || [];
  const total = listQ.data?.total || 0;
  const pages = Math.max(1, Math.ceil(total / LIMIT));
  const meta = metaQ.data || { by_type: {}, countries: [] };

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...EMPTY, ...p, cost_tax_excl: p.cost_tax_excl ?? "", cost_tax_incl: p.cost_tax_incl ?? "" }); setOpen(true); };
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const payload = () => ({
    ...form,
    cost_tax_excl: parseFloat(form.cost_tax_excl) || 0,
    cost_tax_incl: parseFloat(form.cost_tax_incl) || 0,
  });

  const saveMut = useMutation({
    mutationFn: () => editing ? api.put(`/products/${editing.id}`, payload()) : api.post("/products", payload()),
    onSuccess: () => {
      toast.success(editing ? "Producto actualizado" : "Producto creado");
      qc.invalidateQueries({ queryKey: ["products"] });
      qc.invalidateQueries({ queryKey: ["products-meta"] });
      setOpen(false);
    },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });
  const delMut = useMutation({
    mutationFn: (id) => api.delete(`/products/${id}`),
    onSuccess: () => { toast.success("Producto eliminado"); qc.invalidateQueries({ queryKey: ["products"] }); qc.invalidateQueries({ queryKey: ["products-meta"] }); setDelTarget(null); },
    onError: (e) => toast.error(apiErrorMessage(e)),
  });

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {PRODUCT_TYPES.map((t) => {
          const Icon = TYPE_ICON[t];
          return (
            <Card key={t} className="cursor-pointer transition-colors hover:bg-muted/50" onClick={() => { setType(type === t ? "all" : t); setPage(0); }} data-testid={`stat-${t}`}>
              <CardContent className={`flex items-center gap-2 p-3 ${type === t ? "text-primary" : ""}`}>
                <Icon className="h-5 w-5 shrink-0" />
                <div><p className="text-lg font-bold leading-none tabular-nums">{meta.by_type?.[t] ?? 0}</p><p className="text-[11px] text-muted-foreground">{TYPE_LABELS[t]}</p></div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-1 flex-col gap-2 sm:flex-row">
          <div className="relative sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Buscar producto, ciudad, proveedor…" className="pl-8" data-testid="product-search" />
          </div>
          <Select value={type} onValueChange={(v) => { setType(v); setPage(0); }}>
            <SelectTrigger className="sm:w-40" data-testid="product-type-filter"><SelectValue placeholder="Tipo" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {PRODUCT_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={country} onValueChange={(v) => { setCountry(v); setPage(0); }}>
            <SelectTrigger className="sm:w-40" data-testid="product-country-filter"><SelectValue placeholder="País" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los países</SelectItem>
              {(meta.countries || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={openNew} data-testid="new-product-button"><Plus className="mr-1.5 h-4 w-4" />Nuevo producto</Button>
      </div>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="space-y-2 p-4">{Array.from({ length: 8 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}</div>
          ) : items.length === 0 ? (
            <div className="flex flex-col items-center gap-2 p-12 text-center" data-testid="products-empty">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-accent text-accent-foreground"><Package className="h-6 w-6" /></div>
              <p className="font-semibold">Sin productos</p>
              <p className="text-sm text-muted-foreground">Ajusta los filtros o crea uno nuevo.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table data-testid="products-table">
                <TableHeader>
                  <TableRow>
                    <TableHead>Nombre</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead>Proveedor</TableHead>
                    <TableHead>Ciudad</TableHead>
                    <TableHead className="text-right">Coste sin IVA</TableHead>
                    <TableHead className="text-right">Coste con IVA</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((p) => (
                    <TableRow key={p.id} data-testid={`product-row-${p.id}`}>
                      <TableCell className="max-w-[260px] truncate font-medium" title={p.name}>{p.name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-[10px]">{TYPE_LABELS[p.type] || p.type}</Badge></TableCell>
                      <TableCell className="max-w-[160px] truncate text-sm text-muted-foreground" title={p.provider_name || ""}>{p.provider_name || "—"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{p.city || "—"}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEUR(p.cost_tax_excl)}</TableCell>
                      <TableCell className="text-right tabular-nums">{fmtEUR(p.cost_tax_incl)}</TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`} aria-label="Editar"><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => setDelTarget(p)} data-testid={`delete-product-${p.id}`} aria-label="Eliminar"><Trash2 className="h-4 w-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" disabled={page <= 0} onClick={() => setPage((p) => p - 1)} data-testid="products-prev"><ChevronLeft className="h-4 w-4" /></Button>
          <span>Pág. {page + 1} / {pages}</span>
          <Button size="sm" variant="outline" disabled={page + 1 >= pages} onClick={() => setPage((p) => p + 1)} data-testid="products-next"><ChevronRight className="h-4 w-4" /></Button>
        </div>
        <span data-testid="products-count">{total} productos</span>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar producto" : "Nuevo producto"}</DialogTitle>
            <DialogDescription>Producto del catálogo con coste sin y con IVA.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5 sm:col-span-2"><Label>Nombre *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="product-name-input" /></div>
            <div className="space-y-1.5">
              <Label>Tipo</Label>
              <Select value={form.type} onValueChange={(v) => set("type", v)}>
                <SelectTrigger data-testid="product-type-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRODUCT_TYPES.map((t) => <SelectItem key={t} value={t}>{TYPE_LABELS[t]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Proveedor</Label><Input value={form.provider_name || ""} onChange={(e) => set("provider_name", e.target.value)} placeholder="Nombre del proveedor" /></div>
            <div className="space-y-1.5"><Label>Ciudad</Label><Input value={form.city || ""} onChange={(e) => set("city", e.target.value)} /></div>
            <div className="space-y-1.5"><Label>País</Label><Input value={form.country || ""} onChange={(e) => set("country", e.target.value)} data-testid="product-country-input" /></div>
            <div className="space-y-1.5"><Label>Coste sin IVA</Label><Input type="number" step="0.01" value={form.cost_tax_excl} onChange={(e) => set("cost_tax_excl", e.target.value)} data-testid="product-excl-input" /></div>
            <div className="space-y-1.5"><Label>Coste con IVA</Label><Input type="number" step="0.01" value={form.cost_tax_incl} onChange={(e) => set("cost_tax_incl", e.target.value)} data-testid="product-incl-input" /></div>
            <div className="space-y-1.5 sm:col-span-2"><Label>Notas</Label><Textarea value={form.notes || ""} onChange={(e) => set("notes", e.target.value)} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button onClick={() => saveMut.mutate()} disabled={!form.name || saveMut.isPending} data-testid="save-product-button">{saveMut.isPending ? "Guardando…" : "Guardar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent data-testid="delete-product-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar producto?</AlertDialogTitle>
            <AlertDialogDescription>Se eliminará “{delTarget?.name}”. Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={() => delMut.mutate(delTarget.id)} data-testid="confirm-delete-product">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
